import { cva, type VariantProps } from "class-variance-authority";
import { AlertCircle, AlertTriangle, CheckCircle2, Info } from "lucide-react";
import type { HTMLAttributes, ReactNode } from "react";
import { cn } from "@/lib/utils";

export function Card({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn("rounded-xl border bg-card text-card-foreground shadow-sm shadow-black/5", className)} {...props} />;
}

const badgeVariants = cva("inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-semibold ring-1 ring-inset", {
  variants: {
    tone: {
      neutral: "bg-muted text-muted-foreground ring-border",
      success: "bg-success/12 text-success ring-success/35",
      warning: "bg-warning/12 text-warning ring-warning/35",
      danger: "bg-danger/12 text-danger ring-danger/35",
      info: "bg-info/12 text-info ring-info/35",
      violet: "bg-violet/12 text-violet ring-violet/35",
      sky: "bg-sky/12 text-sky ring-sky/35",
      orange: "bg-orange/12 text-orange ring-orange/35",
      demo: "bg-accent/15 text-accent ring-accent/45",
    },
  },
  defaultVariants: { tone: "neutral" },
});

export type BadgeTone = NonNullable<VariantProps<typeof badgeVariants>["tone"]>;

/** dot: durum rozetlerinde renk körlüğüne karşı renk + nokta + metin birlikte. */
export function Badge({ className, tone, dot, children, ...props }: HTMLAttributes<HTMLSpanElement> & VariantProps<typeof badgeVariants> & { dot?: boolean }) {
  return (
    <span className={cn(badgeVariants({ tone }), className)} {...props}>
      {dot && <span aria-hidden className="size-1.5 rounded-full bg-current" />}
      {children}
    </span>
  );
}

const alertIcons = { info: Info, success: CheckCircle2, warning: AlertTriangle, danger: AlertCircle } as const;
const alertTones = {
  info: "border-info/40 bg-info/10",
  success: "border-success/40 bg-success/10",
  warning: "border-warning/40 bg-warning/10",
  danger: "border-danger/40 bg-danger/10",
} as const;

export function Alert({ tone = "info", title, children, className }: { tone?: keyof typeof alertTones; title?: string; children?: ReactNode; className?: string }) {
  const Icon = alertIcons[tone];
  return (
    <div role={tone === "danger" ? "alert" : "status"} className={cn("flex gap-3 rounded-md border p-3 text-sm", alertTones[tone], className)}>
      <Icon className="mt-0.5 size-4 shrink-0" aria-hidden />
      <div className="space-y-1">
        {title && <p className="font-medium">{title}</p>}
        {children && <div className="text-muted-foreground">{children}</div>}
      </div>
    </div>
  );
}

export function Spinner({ label = "Yükleniyor" }: { label?: string }) {
  return (
    <div role="status" className="flex items-center justify-center gap-2 p-8 text-sm text-muted-foreground">
      <span className="size-4 animate-spin rounded-full border-2 border-current border-t-transparent" aria-hidden />
      {label}
    </div>
  );
}

/** Tablo yüklenirken satır iskeleti (içerik kaymasın, bekleme kısa hissettirsin). */
export function TableSkeleton({ rows = 8, label = "Yükleniyor" }: { rows?: number; label?: string }) {
  return (
    <div role="status" aria-label={label} className="divide-y">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="flex items-center gap-4 px-4 py-3.5">
          <span className="size-10 shrink-0 animate-pulse rounded-lg bg-muted" />
          <span className="flex-1 space-y-2">
            <span className="block h-3 w-1/3 animate-pulse rounded bg-muted" />
            <span className="block h-2.5 w-1/2 animate-pulse rounded bg-muted/70" />
          </span>
          <span className="h-3 w-20 animate-pulse rounded bg-muted" />
        </div>
      ))}
      <span className="sr-only">{label}</span>
    </div>
  );
}
